import { inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LFOfflineOrder,
  LFOfflineOrderDto,
  LFOfflineOrderSourceType,
  LiveForm,
  LiveFormFactory,
  ResourceData,
  SAVE_FORM_RESULT
} from '@lfw/roesberg.liveforms.core';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { BehaviorSubject, firstValueFrom, map, Observable, shareReplay, switchMap, take, tap } from 'rxjs';
import { OrdersService } from '../orders.service';

const OFFLINE_ORDERS_STORE_KEY = 'offlineOrders';
const OFFLINE_ORDER_RESOURCES_STORE = 'offline-order-resources';
const CONFLICTED_ORDERS_STORE = 'conflictedOrders';

export interface OfflineOrdersDto {
  [key: string]: LFOfflineOrderDto;
}

export interface OfflineOrders {
  [key: string]: LFOfflineOrder;
}

@Injectable({ providedIn: 'root' })
export class OfflineFormsClient {
  private items$ = new BehaviorSubject<OfflineOrders>({});
  public offlineOrders$ = this.items$.asObservable().pipe(shareReplay(1), takeUntilDestroyed());

  readonly onlyOfflineOrders$: Observable<OfflineOrders> = this.offlineOrders$.pipe(
    map((orders) =>
      Object.entries(orders).reduce((acc, [key, order]) => {
        if (!order.isTemporaryOffline) {
          acc[key] = order;
        }
        return acc;
      }, {} as OfflineOrders)
    )
  );

  readonly onlyTemporaryOfflineOrders$: Observable<OfflineOrders> = this.offlineOrders$.pipe(
    map((orders) =>
      Object.entries(orders).reduce((acc, [key, order]) => {
        if (order.isTemporaryOffline) {
          acc[key] = order;
        }
        return acc;
      }, {} as OfflineOrders)
    )
  );

  private readonly _isLoaded = signal(false);
  private readonly _conflictedOrderIds = signal<Set<string>>(new Set());
  readonly isLoaded = this._isLoaded.asReadonly();

  isConflictedOrder(orderId: string): boolean {
    return this._conflictedOrderIds().has(orderId);
  }

  setOrderConflicted(orderId: string, conflicted: boolean): void {
    if (conflicted === this._conflictedOrderIds().has(orderId)) return;

    this._conflictedOrderIds.update((current) => {
      const next = new Set(current);
      if (conflicted) {
        next.add(orderId);
      } else {
        next.delete(orderId);
      }
      return next;
    });

    if (conflicted) {
      this.#dbService.update<{ orderId: string }>(CONFLICTED_ORDERS_STORE, { orderId }).pipe(take(1)).subscribe();
    } else {
      this.#dbService.delete<{ orderId: string }>(CONFLICTED_ORDERS_STORE, orderId).pipe(take(1)).subscribe();
    }
  }

  #dbService = inject(NgxIndexedDBService);
  readonly #ordersService = inject(OrdersService);

  constructor() {
    this.#dbService
      .getAll<{ orderId: string }>(CONFLICTED_ORDERS_STORE)
      .pipe(take(1))
      .subscribe((items) => {
        const loadedIds = new Set(items.map((item) => item.orderId));
        this._conflictedOrderIds.update((current) => {
          const next = new Set(current);
          loadedIds.forEach((id) => next.add(id));
          return next;
        });
      });

    this.#dbService
      .getAll<LFOfflineOrderDto>(OFFLINE_ORDERS_STORE_KEY)
      .pipe(
        take(1),
        map((orders) =>
          orders.reduce((acc, curr) => {
            acc[curr.id] = new LFOfflineOrder(curr);
            return acc;
          }, {} as OfflineOrders)
        )
      )
      .subscribe((orders) => {
        this.items$.next(orders);
        this._isLoaded.set(true);
      });
  }

  async offlineOrdersCount(): Promise<number> {
    const orders = this.items$.getValue();
    return Object.values(orders).filter((order) => !order.isTemporaryOffline).length;
  }

  async temporaryOfflineOrdersCount(): Promise<number> {
    const orders = this.items$.getValue();
    return Object.values(orders).filter((order) => order.isTemporaryOffline).length;
  }

  async hasOfflineOrders(): Promise<boolean> {
    return (await this.offlineOrdersCount()) > 0;
  }

  async saveOfflineOrder(
    form: LiveForm,
    sourceType: LFOfflineOrderSourceType,
    isTemporaryOffline: boolean
  ): Promise<SAVE_FORM_RESULT> {
    return firstValueFrom(
      this.#dbService
        .update<LFOfflineOrderDto>(
          OFFLINE_ORDERS_STORE_KEY,
          LiveFormFactory.toLiveFormOfflineDto(form, sourceType, isTemporaryOffline)
        )
        .pipe(
          switchMap(() => this.#dbService.getAll<LFOfflineOrderDto>(OFFLINE_ORDERS_STORE_KEY)),
          map((orders) =>
            orders.reduce((acc, curr) => {
              acc[curr.id] = new LFOfflineOrder(curr);
              return acc;
            }, {} as OfflineOrders)
          ),
          tap((orders) => this.items$.next(orders)),
          map(() => SAVE_FORM_RESULT.SAVED_OFFLINE)
        )
    );
  }

  loadResource(fileId: string): Observable<ResourceData> {
    return this.#dbService.getByKey<ResourceData>(OFFLINE_ORDER_RESOURCES_STORE, fileId).pipe(take(1));
  }

  loadResourcesByOrderId(orderId: string): Observable<ResourceData[]> {
    return this.#dbService.getAll<ResourceData>(OFFLINE_ORDER_RESOURCES_STORE).pipe(
      map((resources) => resources.filter((resource) => resource.orderId === orderId)),
      take(1)
    );
  }

  saveResource(resource: ResourceData): Observable<ResourceData> {
    return this.#dbService.update<ResourceData>(OFFLINE_ORDER_RESOURCES_STORE, resource);
  }

  async discardOfflineOrder(orderId: string, projectId: string): Promise<void> {
    if (projectId) {
      await firstValueFrom(this.#ordersService.discardCheckedOutOrder(projectId, orderId));
    }
    const resources = await firstValueFrom(this.loadResourcesByOrderId(orderId));
    await Promise.all(resources.map((resource) => this.deleteResourceByFileId(resource.fileId)));
    await firstValueFrom(this.removeOfflineOrderFromDb(orderId));
  }

  removeOfflineOrderFromDb(orderId: string): Observable<void> {
    return this.#dbService.delete<LFOfflineOrderDto>(OFFLINE_ORDERS_STORE_KEY, orderId).pipe(
      map((orders) =>
        orders.reduce((acc, curr) => {
          acc[curr.id] = new LFOfflineOrder(curr);
          return acc;
        }, {} as OfflineOrders)
      ),
      tap((orders) => this.items$.next(orders)),
      tap(() => this.setOrderConflicted(orderId, false)),
      map(() => void 0)
    );
  }

  async deleteResourceByFileId(fileId: string): Promise<string> {
    await firstValueFrom(this.#dbService.delete<ResourceData>(OFFLINE_ORDER_RESOURCES_STORE, fileId));
    return fileId;
  }

  async getOfflineOrderById(orderId: string): Promise<LFOfflineOrder> {
    return firstValueFrom(this.#dbService.getByKey<LFOfflineOrderDto>(OFFLINE_ORDERS_STORE_KEY, orderId).pipe(take(1)))
      .then((dto) => new LFOfflineOrder(dto))
      .catch(() => ({}) as unknown as LFOfflineOrder);
  }
}
